import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Star } from "lucide-react";

const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";
const CANONICAL = "/texas-professional-practice-buy-in-attorney/";
const SLUG = "texas-professional-practice-buy-in-attorney";

const WHO = [
  ["Physicians", "Associates buying into medical groups, specialty practices, and ambulatory or imaging ventures."],
  ["Dentists & orthodontists", "Associate-to-owner transitions, partial buy-ins, and practices with separate real-estate entities."],
  ["Veterinarians", "Buy-ins to independent hospitals and practices weighing corporate-group offers."],
  ["CPAs & financial professionals", "Partner admissions, capital accounts, and retirement-benefit obligations to senior partners."],
  ["Other licensed professionals", "Engineers, architects, therapists, and other professions organized as PLLCs or professional associations."],
];

const REVIEW = [
  ["Every governing document", "Company Agreement or bylaws, existing buy-sell agreement, employment agreement, building lease, and the documents for any real-estate, equipment, or management entity you are also buying into."],
  ["The valuation formula", "How hard assets, receivables, and goodwill are valued; whose compensation is normalized; and whether the same formula applies when you leave."],
  ["Payment terms", "Cash at closing, a promissory note, or reduced compensation over time \u2014 and what happens to unpaid amounts if you depart early."],
  ["Buyout triggers", "Death, disability, retirement, voluntary departure, termination for cause, loss of license, and divorce \u2014 each with its own price and timing."],
  ["Buyout funding", "Life and disability buyout insurance, who owns the policies, and whether the practice can actually afford its obligations."],
  ["Restrictive covenants", "Non-competes and non-solicits reviewed against current Texas law, including the 2025 limits on physician and health-care covenants."],
  ["Personal guarantees", "Lines of credit, equipment loans, and leases \u2014 the cap, whether liability is joint and several, and release on exit."],
  ["Control and compensation", "Voting thresholds, who sets compensation, admission of new owners, and deadlock procedures."],
  ["Tax structure", "Equity purchase, personal goodwill, or compensation-based vesting \u2014 modeled at entry and at exit with your CPA."],
];

const PRIORITY_FAQ = [
  [
    "When should I bring in an attorney on a practice buy-in?",
    "Before you sign a letter of intent or term sheet. Price, payment terms, and the buyout formula are usually set at that stage, and they are far harder to change once the senior owners consider the deal agreed.",
  ],
  [
    "Can a non-licensed person own part of my Texas professional practice?",
    "Generally, a Texas professional entity must be owned by individuals licensed in that profession, and medical practices are also subject to Texas corporate-practice-of-medicine rules. Ownership by spouses, trusts, or management companies should be reviewed before the buy-in closes.",
  ],
  [
    "Is my practice interest community property?",
    "An interest acquired during marriage is presumptively community property in Texas. The buy-sell agreement should give the practice and the owner the right to buy back any interest awarded to a non-professional spouse, and your marital-property planning should be coordinated with the buy-in.",
  ],
  [
    "Are physician non-competes still enforceable in Texas?",
    "Texas enforces reasonable non-competes, but Texas Business & Commerce Code \u00a7 15.50 imposes additional requirements for physicians, and legislation effective September 1, 2025 further limited physician and certain health-care covenants. Both your employment agreement and the Company Agreement should be reviewed against the statute in effect when you sign.",
  ],
];

const RELATED = [
  ["Buying Into a Professional Practice? (Journal)", "/journal/buying-into-a-professional-practice-texas", "The pre-signing checklist for Texas physicians, dentists, CPAs, and other licensed professionals."],
  ["Physician Practice Sales", "/texas-physician-practice-sale-attorney/", "Selling or transitioning a Texas medical, dental, or veterinary practice."],
  ["Business Succession Planning", "/texas-business-succession-planning-attorney/", "Buy-sell agreements, ownership transitions, and succession for Texas businesses."],
  ["Texas LLC & PLLC Formation", "/texas-llc-formation-attorney/", "Entity selection and company agreements drafted for the owners actually involved."],
];

const Section = ({ id, bg = "bg-ivory", kicker, title, intro, children }) => (
  <section id={id} className={`${bg} py-20 md:py-28`} data-testid={`buyin-${id}`}>
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="kicker">{kicker}</div>
        <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">{title}</h2>
        {intro && <p className="mt-6 text-slate2 font-serif text-lg leading-relaxed">{intro}</p>}
      </div>
      <div className="lg:col-span-7">{children}</div>
    </div>
  </section>
);

const TermList = ({ items, prefix }) => (
  <dl className="space-y-6">
    {items.map(([title, body], i) => (
      <div key={title} className="hairline-top pt-5" data-testid={`${prefix}-${i}`}>
        <dt className="font-display text-xl md:text-2xl text-navy flex items-center gap-3">
          <Star size={14} className="text-brass" aria-hidden="true" />
          {title}
        </dt>
        <dd className="mt-2 text-slate2 font-serif leading-relaxed">{body}</dd>
      </div>
    ))}
  </dl>
);

function FaqList() {
  const [open, setOpen] = useState(null);
  const toggle = (i, q) => {
    const wasOpen = open === i;
    setOpen(wasOpen ? null : i);
    if (!wasOpen) trackPriorityFaqOpen({ page_slug: SLUG, faq_position: i + 1, faq_question: q });
  };
  return (
    <ul className="mt-12 space-y-6" data-testid="buyin-faq-list">
      {PRIORITY_FAQ.map(([q, a], i) => (
        <li key={q} className="hairline-top pt-5" data-testid={`buyin-faq-${i}`}>
          <button className="w-full flex items-start justify-between gap-6 text-left group" onClick={() => toggle(i, q)} aria-expanded={open === i} data-testid={`buyin-faq-toggle-${i}`}>
            <span className="font-display text-lg md:text-2xl text-navy group-hover:text-oxblood transition">{q}</span>
            <ChevronDown size={20} className={`text-brass transition-transform mt-1 ${open === i ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {open === i && <p className="mt-4 text-slate2 font-serif leading-relaxed" data-testid={`buyin-faq-answer-${i}`}>{a}</p>}
        </li>
      ))}
    </ul>
  );
}

const CallLink = ({ testid, light }) => (
  <a href={`tel:${PHONE_TEL}`} className={light ? "btn-ghost-light" : "btn-ghost"} data-testid={testid}>
    <Phone size={16} className="inline mr-2" />Call {PHONE}
  </a>
);

export default function PracticeBuyIn() {
  useEffect(() => {
    trackAuthorityPageView({ page_slug: SLUG, page_title: "Texas Professional Practice Buy-In Attorney", priority_faq_count: PRIORITY_FAQ.length });
  }, []);

  return (
    <div className="bg-ivory" data-testid="practice-buyin-page">
      <SEO
        title="Texas Professional Practice Buy-In Attorney | Lone Star Counsel"
        description="Counsel for Texas physicians, dentists, veterinarians, CPAs, and other licensed professionals buying into a practice. Valuation, buy-sell terms, non-competes, guarantees, and tax structure reviewed before you sign. Call (972) 712-1515."
        path={CANONICAL}
        schema={[
          legalServiceSchema({ name: "Texas Professional Practice Buy-In Counsel", description: "Review and negotiation of professional-practice buy-ins for Texas licensed professionals.", url: CANONICAL, area: "Texas" }),
          attorneySchema(),
          faqPageSchema(PRIORITY_FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Practice", url: "/practice" },
            { name: "Professional Practice Buy-Ins", url: CANONICAL },
          ]),
        ]}
      />

      <section data-testid="buyin-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="kicker">Lone Star &#9733; Counsel</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy" data-testid="buyin-h1">
              Buying Into a Texas<br />
              <span className="italic text-oxblood">Professional Practice.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate2 font-serif leading-relaxed" data-testid="buyin-direct-answer">
              The day you buy in is the best day you will ever have to negotiate how you get out. Lone Star Counsel reviews and negotiates buy-ins for Texas physicians, dentists, veterinarians, CPAs, and other licensed professionals &mdash; price, buyout terms, non-competes, guarantees, and tax structure &mdash; before you sign.
            </p>
            <AttorneyReviewNote reviewed="October 2026" />
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/intake" className="btn-primary" data-testid="buyin-hero-primary">Request a Buy-In Review</Link>
              <CallLink testid="buyin-hero-call" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src="/img/journal-buysell.jpg" alt="Partners reviewing a professional practice buy-in agreement." className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <Section id="who" kicker="Who We Advise" title="Licensed professionals becoming owners." intro="Each profession has its own licensing, ownership, and covenant rules. The buy-in documents should reflect them.">
        <TermList items={WHO} prefix="buyin-who" />
      </Section>

      <Section id="review" bg="bg-linen" kicker="Before You Sign" title="What we review in a buy-in." intro="A buy-in is rarely a single purchase. We review every entity and every document you are signing into.">
        <TermList items={REVIEW} prefix="buyin-review" />
      </Section>

      <section id="faqs" className="bg-ivory py-20 md:py-28" data-testid="buyin-faqs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">Practice buy-in questions.</h2>
          <FaqList />
        </div>
      </section>

      <section id="related" className="bg-linen py-20 md:py-28" data-testid="buyin-related">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker">Also at the Firm</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">Related reading and practice areas.</h2>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {RELATED.map(([label, href, desc], i) => (
              <li key={href} className="hairline-top pt-5" data-testid={`buyin-related-${i}`}>
                <Link to={href} className="group block">
                  <div className="font-display text-xl md:text-2xl text-navy group-hover:text-oxblood transition">{label}</div>
                  <div className="mt-2 text-slate2 font-serif text-sm leading-relaxed">{desc}</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="contact" className="panel-navy py-20 md:py-28" data-testid="buyin-contact">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <div className="kicker">Lone Star &#9733; Counsel</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-ivory leading-[1.0]">Plan your buyout before you sign.</h2>
          <p className="mt-6 max-w-2xl mx-auto text-ivory/80 font-serif text-lg leading-relaxed">
            Nearly 30 years as both an attorney and a CPA &mdash; reviewing the legal, tax, and valuation sides of a buy-in together.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/intake" className="btn-brass" data-testid="buyin-contact-primary">Request a Buy-In Review</Link>
            <CallLink testid="buyin-contact-call" light />
          </div>
          <p className="mt-6 text-ivory/60 font-serif text-sm">LONE STAR &#9733; COUNSEL is a dba of Pratt Law Group, PLLC. This page is general information, not legal advice.</p>
        </div>
      </section>
    </div>
  );
}
